/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { css } from "@emotion/css";
import { Level2PageLayout, Tooltip } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import { repository } from "~/clientInstance";
import DataBaseComponent from "~/components/DataBaseComponent";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import type { CreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import type { GlobalState } from "~/store/reducers";
import { actionTemplateFetch, newActionTemplateSelected } from "../../../reducers/libraryArea";
interface ActionTemplateLayoutInternalProps {
    spaceId: string;
    templateId: string;
    actionTemplateName?: string;
    actionTemplateLogo?: string;
    actionTemplatePendingUpdates?: number;
    onFetchActionTemplate(actionTemplate: ActionTemplateResource): void;
    onNewActionTemplateSelected(): void;
    createPageSideNavLink: CreatePageSideNavLink;
}
class ActionTemplateLayoutInternal extends DataBaseComponent<ActionTemplateLayoutInternalProps, never> {
    constructor(props: ActionTemplateLayoutInternalProps) {
        super(props);
    }
    componentDidMount() {
        if (this.props.templateId) {
            this.doBusyTask(async () => {
                const actionTemplate = await repository.ActionTemplates.get(this.props.templateId);
                this.props.onFetchActionTemplate(actionTemplate);
            });
        }
        else {
            this.props.onNewActionTemplateSelected();
        }
    }
    render() {
        const navLinks = [];
        if (this.props.templateId) {
            navLinks.push(this.props.createPageSideNavLink("Details", links.editStepTemplatePage.generateUrl({ spaceId: this.props.spaceId, templateId: this.props.templateId }), true)!);
            navLinks.push(this.props.createPageSideNavLink(<UsageNavbarItemLabel totalPendingUpdates={this.props.actionTemplatePendingUpdates}/>, links.stepTemplateUsagePage.generateUrl({ spaceId: this.props.spaceId, templateId: this.props.templateId }))!);
        }
        if (this.props.templateId && !this.props.actionTemplateName) {
            return (<main>
                    <SkeletonLoadingLayout errors={this.errors}/>
                </main>);
        }
        if (!this.props.templateId) {
            return (<main>
                    <Level2PageLayout content={this.props.children}/>
                </main>);
        }
        return (<main>
                <Level2PageLayout navItems={navLinks} content={this.props.children} header={{
                title: this.props.actionTemplateName || "New step template",
                breadcrumbs: [{ label: "Step Templates", pageUrl: links.stepTemplatesPage.generateUrl({ spaceId: this.props.spaceId }) }],
                logo: this.props.actionTemplateLogo ? { href: this.props.actionTemplateLogo, accessibleName: "Logo" } : undefined,
            }}/>
            </main>);
    }
    static displayName = "ActionTemplateLayoutInternal";
}
interface UsageWithPendingUpdatesLabelProps {
    totalPendingUpdates: number | undefined;
}
function UsageNavbarItemLabel({ totalPendingUpdates = 0 }: UsageWithPendingUpdatesLabelProps) {
    return (<div className={usageWithPendingUpdatesLabelStyles}>
            Usage
            {totalPendingUpdates > 0 && <PendingUsages totalPendingUpdates={totalPendingUpdates}/>}
        </div>);
}
function PendingUsages({ totalPendingUpdates }: React.PropsWithChildren<{
    totalPendingUpdates: number;
}>) {
    const tooltipContent = `${totalPendingUpdates} action ${pluralize(totalPendingUpdates, "template usage has", "template usages have")} pending changes.`;
    return (<Tooltip content={tooltipContent}>
            <em className={"fa-solid fa-triangle-exclamation"} style={{ color: themeTokens.color.callout.text.warning }}/>
        </Tooltip>);
}
function pluralize(count: number, singular: string, plural: string) {
    return count > 1 ? plural : singular;
}
const usageWithPendingUpdatesLabelStyles = css({
    display: "flex",
    flexDirection: "row",
    gap: space[8],
    alignItems: "center",
});
interface ActionTemplateLayoutProps {
    children: React.ReactNode;
}
export default function ActionTemplateLayout({ children }: ActionTemplateLayoutProps) {
    const { spaceId, templateId } = useParams<{
        spaceId: string;
        templateId: string;
    }>();
    const dispatch: ThunkDispatch<GlobalState, void, AnyAction> = useDispatch();
    const currentActionTemplate = useSelector((state: GlobalState) => {
        const { currentActionTemplate } = state.libraryArea;
        // The current action template in redux state may not be the one we want.
        // Checking the id matches will prevent the incorrect template being rendered.
        // Ideally we should clear this redux state at an appropriate time or better yet, remove the redux dependency here.
        return currentActionTemplate?.id === templateId ? currentActionTemplate : undefined;
    });
    const dispatchFetchActionTemplate = React.useCallback((actionTemplate: ActionTemplateResource) => {
        dispatch(actionTemplateFetch(actionTemplate));
    }, [dispatch]);
    const dispatchNewActionTemplateSelected = React.useCallback(() => {
        dispatch(newActionTemplateSelected());
    }, [dispatch]);
    const createPageSideNavLink = useCreatePageSideNavLink();
    return (<ActionTemplateLayoutInternal spaceId={spaceId} templateId={templateId} actionTemplateName={currentActionTemplate?.name} actionTemplateLogo={currentActionTemplate?.logo} actionTemplatePendingUpdates={currentActionTemplate?.pendingUpdates} onFetchActionTemplate={dispatchFetchActionTemplate} onNewActionTemplateSelected={dispatchNewActionTemplateSelected} createPageSideNavLink={createPageSideNavLink}>
            {children}
        </ActionTemplateLayoutInternal>);
}
